import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Globe,
  Shield,
  ShieldCheck,
  AlertTriangle,
  CheckCircle,
  Check,
  Copy,
  RefreshCw,
  MapPin,
  Server,
  Wifi,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Clock,
  Stethoscope,
  Scale,
  Info
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

export default function Tools() {
  const [ipData, setIpData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchIP = async () => {
    setLoading(true);
    setError(null);
    try {
      // Using ipapi.co for IP geolocation (free tier available)
      const response = await fetch("https://ipapi.co/json/");
      if (!response.ok) throw new Error("Failed to fetch IP data");
      const data = await response.json();
      setIpData(data);
    } catch (err) {
      setError("Unable to fetch IP information. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIP();
  }, []);

  const copyToClipboard = () => {
    if (ipData?.ip) {
      navigator.clipboard.writeText(ipData.ip);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isVPNLikely = ipData?.org?.toLowerCase().includes("vpn") ||
                       ipData?.org?.toLowerCase().includes("proxy") ||
                       ipData?.org?.toLowerCase().includes("hosting");

  return (
    <>
      <Helmet>
        <title>What Is My IP Address? Network Exposure Check | SACVPN</title>
        <meta
          name="description"
          content="Free IP address lookup and network exposure check. See your public IP, location, and ISP - and what that exposure means if you handle PHI, client files, or other regulated data. Protect it with SACVPN."
        />
        <meta
          name="keywords"
          content="what is my IP, IP address lookup, check my IP, IP location, my public IP, IP checker, VPN test, privacy check, network exposure check, HIPAA IP exposure, PHI network security, client confidentiality VPN"
        />
        <link rel="canonical" href="https://www.sacvpn.com/tools" />
        <meta property="og:title" content="What Is My IP Address? Network Exposure Check | SACVPN" />
        <meta property="og:description" content="Free IP address lookup and network exposure check. See your public IP, location, and ISP - and what that exposure means if you handle PHI, client files, or other regulated data. Protect it with SACVPN." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "SACVPN IP Lookup & Network Exposure Check Tool",
            "description": "Free tool to check your public IP address, location, and connection exposure - including what that exposure means if you handle PHI, client files, or other regulated data.",
            "applicationCategory": "SecurityApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "provider": {
              "@type": "Organization",
              "name": "SACVPN"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-dark-900" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_460px_at_50%_-5%,rgba(14,165,233,0.12),transparent_65%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
              Free network exposure check
            </motion.p>

            <motion.h1
              variants={fadeUp}
              className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white leading-[1.05] tracking-tight"
            >
              What is my{" "}
              <span className="text-brand-400">IP address?</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mt-6 text-lg text-gray-400 leading-relaxed"
            >
              Check your public IP address, location, and ISP - the same information a hospital guest network, courthouse Wi-Fi, or coffee-shop hotspot can log about the device you're using right now. If you handle patient records, client files, or other regulated or privileged data on the go, this exposure is more than a privacy curiosity - it's exactly the kind of identifying information data-protection rules like HIPAA were written around.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* IP Display Section */}
      <section className="py-16 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-4xl mx-auto"
          >
            {/* Main IP Card */}
            <div className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-8 md:p-12">
              {loading ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-12 h-12 text-brand-400 animate-spin mx-auto mb-4" />
                  <p className="text-gray-400">Detecting your IP address...</p>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">{error}</p>
                  <button
                    onClick={fetchIP}
                    className="px-6 py-3 bg-brand-500 text-white rounded-lg font-medium hover:bg-brand-400 transition-colors"
                  >
                    Try again
                  </button>
                </div>
              ) : (
                <>
                  {/* IP Address Display */}
                  <div className="text-center mb-8">
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-gray-500 mb-3">Your public IP address</p>
                    <div className="flex items-center justify-center gap-4">
                      <h2 className="text-4xl md:text-5xl font-mono font-bold text-white">
                        {ipData?.ip || "Unknown"}
                      </h2>
                      <button
                        onClick={copyToClipboard}
                        className="p-3 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
                        title="Copy IP address"
                      >
                        {copied ? (
                          <CheckCircle className="w-6 h-6 text-brand-400" />
                        ) : (
                          <Copy className="w-6 h-6 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {copied && (
                      <p className="text-brand-400 text-sm mt-2">Copied to clipboard.</p>
                    )}
                  </div>

                  {/* Status Badge */}
                  <div className="flex justify-center mb-8">
                    {isVPNLikely ? (
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-500/10 border border-brand-500/30 rounded-lg">
                        <Shield className="w-5 h-5 text-brand-400" />
                        <span className="text-brand-300 font-medium">VPN or proxy detected</span>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                        <span className="text-red-300 font-medium">Your real IP is exposed</span>
                      </div>
                    )}
                  </div>

                  {/* IP Details Grid */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-dark-900 rounded-lg p-4 border border-white/[0.06]">
                      <div className="flex items-center gap-3 mb-2">
                        <MapPin className="w-5 h-5 text-brand-400" />
                        <span className="text-gray-500 text-sm">Location</span>
                      </div>
                      <p className="text-white font-medium">
                        {ipData?.city || "Unknown"}, {ipData?.region || ""} {ipData?.country_name || ""}
                      </p>
                    </div>

                    <div className="bg-dark-900 rounded-lg p-4 border border-white/[0.06]">
                      <div className="flex items-center gap-3 mb-2">
                        <Server className="w-5 h-5 text-brand-400" />
                        <span className="text-gray-500 text-sm">ISP / organization</span>
                      </div>
                      <p className="text-white font-medium truncate">
                        {ipData?.org || "Unknown"}
                      </p>
                    </div>

                    <div className="bg-dark-900 rounded-lg p-4 border border-white/[0.06]">
                      <div className="flex items-center gap-3 mb-2">
                        <Globe className="w-5 h-5 text-brand-400" />
                        <span className="text-gray-500 text-sm">Country code</span>
                      </div>
                      <p className="text-white font-medium">
                        {ipData?.country_code || "Unknown"}
                      </p>
                    </div>

                    <div className="bg-dark-900 rounded-lg p-4 border border-white/[0.06]">
                      <div className="flex items-center gap-3 mb-2">
                        <Wifi className="w-5 h-5 text-brand-400" />
                        <span className="text-gray-500 text-sm">IP version</span>
                      </div>
                      <p className="text-white font-medium">
                        {ipData?.version || (ipData?.ip?.includes(":") ? "IPv6" : "IPv4")}
                      </p>
                    </div>
                  </div>

                  {/* Refresh Button */}
                  <div className="text-center mt-6">
                    <button
                      onClick={fetchIP}
                      className="inline-flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white transition-colors"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Refresh
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>

          {/* Regulated & Privileged Data Context Module */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-4xl mx-auto mt-8"
          >
            <div className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-8 md:p-10">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-3">For regulated & privileged data</p>
              <h2 className="text-xl md:text-2xl font-display font-bold text-white tracking-tight mb-3">
                If you handle PHI or client data, here's what this exposure means
              </h2>
              <p className="text-gray-400 leading-relaxed mb-8">
                The address above isn't just a privacy curiosity for healthcare staff, legal teams, and anyone working with regulated or privileged data on the go. Here's the specific reasoning - and where SACVPN fits as one control in your safeguard program, not a certification.
              </p>

              <div className="grid sm:grid-cols-2 gap-5">
                {[
                  {
                    icon: Stethoscope,
                    title: "Your IP is a named HIPAA identifier",
                    description: "Under HIPAA's Safe Harbor de-identification standard (45 CFR 164.514(b)(2)(i)(O)), \"IP address\" is one of the 18 identifiers that must be removed for data to count as de-identified. An IP tied to a telehealth session or patient portal login is treated by HIPAA's own framework as identifying information.",
                  },
                  {
                    icon: Lock,
                    title: "Encryption is the breach-notification safe harbor",
                    description: "Under the HITECH Breach Notification Rule, PHI rendered unusable, unreadable, or indecipherable to unauthorized persons generally isn't \"unsecured PHI.\" Intercepted traffic on a clinic guest network or public Wi-Fi can trigger notification duties; an always-on encrypted tunnel supports the Security Rule's Transmission Security safeguard (45 CFR 164.312(e)(1)).",
                  },
                  {
                    icon: Scale,
                    title: "The same risk applies to privileged client data",
                    description: "Attorneys carry an analogous ethical duty. ABA Model Rule 1.6 and state-bar cybersecurity opinions call for reasonable efforts to prevent unauthorized disclosure of client confidences, including over public networks. An exposed IP on courthouse or hotel Wi-Fi is the same risk, whether the data is PHI or client PII.",
                  },
                  {
                    icon: ShieldCheck,
                    title: "\"HIPAA certified\" doesn't exist, so we won't say it",
                    description: "HHS runs no official HIPAA certification program for any product or vendor - it's a common but false claim among VPN competitors. What we can say plainly: our WireGuard encryption and IP masking are safeguards you can document in your own HIPAA risk analysis and Security Rule technical-safeguards section.",
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="rounded-lg border border-white/[0.06] bg-dark-900 p-5"
                  >
                    <div className="w-10 h-10 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-4">
                      <item.icon className="w-5 h-5 text-brand-400" />
                    </div>
                    <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>

              {/* Disclaimer */}
              <div className="mt-8 pt-6 border-t border-white/[0.06]">
                <p className="text-xs text-gray-500 flex items-start gap-2 leading-relaxed">
                  <Info className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <span>
                    SACVPN is not a HIPAA-certified product (no such federal certification exists) and does not currently offer a signed Business Associate Agreement. Our encryption and IP-masking features are technical safeguards you can use as part of your organization's own HIPAA Security Rule compliance program.
                  </span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Privacy Warning Section */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Exposure</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              What your IP address reveals
            </h2>
            <p className="mt-4 text-gray-400 leading-relaxed">
              Without a VPN, websites, advertisers, and attackers can see this information about you.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {[
              {
                icon: MapPin,
                title: "Physical location",
                description: "Your city, region, and country can be determined from your IP address.",
              },
              {
                icon: Server,
                title: "Internet provider",
                description: "Your ISP is visible, which can be used to identify your household.",
              },
              {
                icon: Eye,
                title: "Browsing activity",
                description: "Websites track your IP to build a profile of your online behavior.",
              },
              {
                icon: Globe,
                title: "Network type",
                description: "Whether you're on home Wi-Fi, mobile data, or public networks.",
              },
              {
                icon: AlertTriangle,
                title: "Security risks",
                description: "Attackers can target your IP for DDoS attacks or intrusion attempts.",
              },
              {
                icon: EyeOff,
                title: "No privacy",
                description: "Every website you visit logs your IP address in their server records.",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeUp}
                className="rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                  <item.icon className="w-5 h-5 text-brand-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How VPN Protects Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">One control, not a cure-all</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              How an encrypted tunnel fits your safeguard program
            </h2>
            <p className="mt-4 text-gray-400 leading-relaxed">
              SACVPN masks your real IP address and encrypts your connection with WireGuard - one control you can document alongside your organization's other HIPAA Security Rule and confidentiality safeguards.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-8 md:p-12"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-xl font-semibold text-white mb-6">With SACVPN</h3>
                <ul className="space-y-4">
                  {[
                    "Your real IP is hidden from websites",
                    "Your traffic is encrypted with WireGuard",
                    "Your ISP can't see what you browse",
                    "You appear to be in a different location",
                    "Attackers can't target your real IP",
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-400">
                      <Check className="w-5 h-5 text-brand-400 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center">
                <div className="inline-block rounded-xl border border-brand-500/20 bg-brand-500/[0.06] p-8">
                  <Lock className="w-14 h-14 text-brand-400 mx-auto mb-4" />
                  <p className="text-white font-semibold text-lg mb-2">Your IP with VPN</p>
                  <p className="text-brand-400 font-mono text-lg">Protected</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-4xl mx-auto"
          >
            <h3 className="text-2xl font-display font-bold text-white tracking-tight text-center mb-8">Learn more about network security and compliance</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Link to="/blog/what-is-vpn-complete-guide" className="block rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors">
                <h4 className="font-semibold text-white mb-2">What is a VPN?</h4>
                <p className="text-gray-400 text-sm leading-relaxed">Complete guide to VPN technology and how it protects you.</p>
              </Link>
              <Link to="/blog/public-wifi-security-risks" className="block rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors">
                <h4 className="font-semibold text-white mb-2">Public Wi-Fi dangers</h4>
                <p className="text-gray-400 text-sm leading-relaxed">Why you need a VPN on public networks.</p>
              </Link>
              <Link to="/blog/protect-privacy-online-2025" className="block rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors">
                <h4 className="font-semibold text-white mb-2">Online privacy guide</h4>
                <p className="text-gray-400 text-sm leading-relaxed">Practical tips to protect your privacy online.</p>
              </Link>
              <Link to="/industries/healthcare" className="block rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors">
                <h4 className="font-semibold text-white mb-2">VPN for healthcare</h4>
                <p className="text-gray-400 text-sm leading-relaxed">How WireGuard encryption supports HIPAA technical safeguards for clinicians and staff.</p>
              </Link>
              <Link to="/industries/legal" className="block rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors">
                <h4 className="font-semibold text-white mb-2">VPN for legal teams</h4>
                <p className="text-gray-400 text-sm leading-relaxed">Protect privileged client data and support your duty of confidentiality on the go.</p>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-dark-900 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="relative overflow-hidden rounded-2xl border border-brand-500/20 bg-[radial-gradient(700px_320px_at_50%_-10%,rgba(14,165,233,0.18),transparent_70%)] bg-dark-800 px-8 md:px-14 py-16 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-4">
              See how this fits your safeguard program
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-9">
              Mask your IP and encrypt your connection with SACVPN - one control you can document in your organization's own HIPAA Security Rule safeguards or client-confidentiality program. Start your 14-day free trial.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
              >
                <Clock className="w-5 h-5" />
                Start free trial
              </Link>
              <Link
                to="/industries/healthcare"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
              >
                See HIPAA safeguards
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
